import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  Customer,
  CustomerCreateInput,
  CustomerCreateOptions,
  CustomerDuplicateMatch,
  CustomerService,
  CustomerStateOption,
  CustomerTagOption,
  GstTreatmentOption,
} from '../../services/customer';
import { CustomerSelectionService } from '../../services/customer-selection';
import { NotificationService } from '../../services/notification';
import { Pricelist, PricelistService } from '../../services/price-lists';
import { Route, RouteSegmentSummary } from '../../services/routes';
import { RouteSelectionService } from '../../services/route-selection';

@Component({
  selector: 'app-new-customer',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule],
  templateUrl: './new-customer-component.html',
  styleUrls: ['./new-customer-component.scss']
})
export class NewCustomerComponent implements OnInit {
  selectedRoute: Route | null = null;
  segments: RouteSegmentSummary[] = [];
  priceLists: Pricelist[] = [];
  selectedSegmentId: number | null = null;
  selectedPriceListId: number | null = null;

  createOptions: CustomerCreateOptions | null = null;
  states: CustomerStateOption[] = [];
  tags: CustomerTagOption[] = [];
  gstTreatments: GstTreatmentOption[] = [];
  countryId: number | null = null;
  countryName = '';
  selectedStateId: number | null = null;
  selectedTagIds: number[] = [];
  gstTreatment = 'unregistered';
  tagsOpen = false;
  loadingOptions = false;
  gstLookupBusy = false;
  gstLookupMessage = '';
  gstLookupError = '';

  clientCustomerUuid = uuidv4();
  name = '';
  phone = '';
  whatsappNumber = '';
  vat = '';
  street = '';
  street2 = '';
  city = '';
  zip = '';

  latitude: number | null = null;
  longitude: number | null = null;
  gpsAccuracyM: number | null = null;
  gpsCapturedAt: string | null = null;
  locating = false;
  locationError = '';

  image = '';
  imageMimeType = 'image/jpeg';
  imagePreview = '';
  photoError = '';

  duplicateMatches: CustomerDuplicateMatch[] = [];
  checkingDuplicates = false;
  saving = false;
  formError = '';
  validationAttempted = false;

  constructor(
    private router: Router,
    private routeSelection: RouteSelectionService,
    private customerService: CustomerService,
    private customerSelection: CustomerSelectionService,
    private priceListService: PricelistService,
    private notification: NotificationService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.selectedRoute = this.routeSelection.getSelected();
    if (!this.selectedRoute) {
      this.formError = 'Select a route before creating a customer.';
      return;
    }

    this.segments = this.selectedRoute.segments?.length
      ? [...this.selectedRoute.segments]
      : (this.selectedRoute.lineIds || []).map((id, index) => ({ id, name: `Segment ${index + 1}`, sequence: index + 1 }));
    this.selectedSegmentId = this.segments[0]?.id || null;

    this.loadingOptions = true;
    try {
      const [priceLists, createOptions] = await Promise.all([
        firstValueFrom(this.priceListService.get()),
        this.customerService.getCreateOptions(),
      ]);
      this.priceLists = priceLists;
      const routeDefault = this.selectedRoute.defaultPricelistId || null;
      this.selectedPriceListId = routeDefault && this.priceLists.some(p => p.id === routeDefault)
        ? routeDefault
        : null;

      this.createOptions = createOptions;
      this.states = createOptions.states || [];
      this.tags = createOptions.tags || [];
      this.gstTreatments = createOptions.gstTreatments || [];
      this.countryId = createOptions.countryId;
      this.countryName = createOptions.countryName || '';
      this.selectedStateId = createOptions.defaultStateId || null;
      this.gstTreatment = createOptions.defaultGstTreatment || 'unregistered';

      // Default to the existing Odoo 'Retail' tag when available.
      const retailTag = this.tags.find(tag => (tag.name || '').trim().toLocaleLowerCase() === 'retail');
      if (retailTag && !this.selectedTagIds.length) {
        this.selectedTagIds = [retailTag.id];
      }
    } catch (err: any) {
      console.error('Customer setup options could not be loaded.', err);
      this.formError = err?.error?.message || err?.message || 'Unable to load customer setup options.';
    } finally {
      this.loadingOptions = false;
    }
  }

  captureCurrentLocation(): void {
    this.locationError = '';
    if (!navigator.geolocation) {
      this.locationError = 'Location is not supported on this device.';
      return;
    }

    this.locating = true;
    navigator.geolocation.getCurrentPosition(
      position => {
        this.locating = false;
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
        this.gpsAccuracyM = position.coords.accuracy;
        this.gpsCapturedAt = new Date(position.timestamp || Date.now()).toISOString();
      },
      error => {
        this.locating = false;
        this.locationError = error.message || 'Unable to capture current location.';
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  }

  openCapturedLocation(): void {
    if (this.latitude == null || this.longitude == null) return;
    window.open(`https://www.google.com/maps/search/?api=1&query=${this.latitude},${this.longitude}`, '_blank');
  }

  async onPhotoSelected(event: Event): Promise<void> {
    this.photoError = '';
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.photoError = 'Please choose an image file.';
      return;
    }

    try {
      const compressed = await this.compressImage(file, 1440, 0.82);
      this.image = compressed.base64;
      this.imageMimeType = compressed.mimeType;
      this.imagePreview = `data:${compressed.mimeType};base64,${compressed.base64}`;
    } catch (err) {
      console.error(err);
      this.photoError = 'Unable to prepare the shop photo.';
    }
  }

  clearPhoto(): void {
    this.image = '';
    this.imagePreview = '';
    this.photoError = '';
  }

  onCustomerNameChange(value: string): void {
    this.name = (value || '').toLocaleUpperCase();
  }

  get selectedTagSummary(): string {
    if (!this.selectedTagIds.length) return 'Select customer tag(s)';
    const names = this.tags.filter(tag => this.selectedTagIds.includes(tag.id)).map(tag => tag.name);
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }

  get gstNumberRequired(): boolean {
    return !!this.gstTreatments.find(item => item.value === this.gstTreatment)?.requiresVat;
  }

  get validationMessages(): string[] {
    const errors: string[] = [];
    const gstin = this.vat.trim().toUpperCase().replace(/\s+/g, '');

    if (!this.name.trim()) errors.push('Customer name is required');
    if (!this.phone.trim()) errors.push('Phone number is required');
    if (!this.selectedTagIds.length) errors.push('At least one customer tag is required');
    if (!this.selectedRoute || !this.selectedSegmentId) errors.push('Route segment is required');
    if (!this.selectedPriceListId) errors.push('Pricelist is required');
    if (!this.gstTreatment) errors.push('GST Treatment is required');
    if (this.gstNumberRequired && !gstin) {
      errors.push('GST number is required for the selected GST Treatment');
    } else if (gstin && gstin.length !== 15) {
      errors.push('GST number must contain 15 characters');
    }

    return errors;
  }

  get nameInvalid(): boolean {
    return this.validationAttempted && !this.name.trim();
  }

  get phoneInvalid(): boolean {
    return this.validationAttempted && !this.phone.trim();
  }

  get tagsInvalid(): boolean {
    return this.validationAttempted && !this.selectedTagIds.length;
  }

  get segmentInvalid(): boolean {
    return this.validationAttempted && (!this.selectedRoute || !this.selectedSegmentId);
  }

  get pricelistInvalid(): boolean {
    return this.validationAttempted && !this.selectedPriceListId;
  }

  get gstTreatmentInvalid(): boolean {
    return this.validationAttempted && !this.gstTreatment;
  }

  get vatInvalid(): boolean {
    if (!this.validationAttempted) return false;
    const gstin = this.vat.trim().toUpperCase().replace(/\s+/g, '');
    return (this.gstNumberRequired && !gstin) || (!!gstin && gstin.length !== 15);
  }

  isTagSelected(tagId: number): boolean {
    return this.selectedTagIds.includes(tagId);
  }

  toggleTag(tagId: number, checked: boolean): void {
    const values = new Set(this.selectedTagIds);
    checked ? values.add(tagId) : values.delete(tagId);
    this.selectedTagIds = [...values];
  }

  async lookupGstin(): Promise<void> {
    this.formError = '';
    this.gstLookupMessage = '';
    this.gstLookupError = '';
    const gstin = this.vat.trim().toUpperCase().replace(/\s+/g, '');
    if (!gstin) {
      this.gstLookupError = 'Enter the GST number before fetching details.';
      return;
    }
    if (gstin.length !== 15) {
      this.gstLookupError = 'GST number must contain 15 characters.';
      return;
    }

    this.gstLookupBusy = true;
    try {
      const response = await this.customerService.lookupGstin(gstin);
      if (response.status !== 'success') {
        throw new Error(response.message || 'GST lookup failed.');
      }
      const customer = response.customer || {};
      this.vat = gstin;
      if (customer.name) this.name = customer.name.toLocaleUpperCase();
      if (customer.phone && !this.phone.trim()) this.phone = customer.phone;
      if (customer.street) this.street = customer.street;
      if (customer.street2) this.street2 = customer.street2;
      if (customer.city) this.city = customer.city;
      if (customer.zip) this.zip = customer.zip;
      if (customer.countryId) this.countryId = customer.countryId;
      if (customer.stateId && this.states.some(state => state.id === customer.stateId)) {
        this.selectedStateId = customer.stateId;
      }
      this.gstLookupMessage = response.message || (response.found ? 'Customer details loaded.' : 'No customer details were returned.');
    } catch (err: any) {
      this.gstLookupError = err?.error?.message || err?.message || 'Unable to fetch GST customer details.';
    } finally {
      this.gstLookupBusy = false;
    }
  }

  async saveCustomer(createAnyway = false): Promise<void> {
    this.formError = '';
    this.validationAttempted = true;
    this.tagsOpen = false;

    if (this.validationMessages.length) {
      return;
    }

    const payload = this.buildPayload();

    if (!createAnyway) {
      this.checkingDuplicates = true;
      try {
        this.duplicateMatches = await this.customerService.suggestDuplicates(payload);
      } catch (err) {
        console.warn('Duplicate assistance is unavailable; creation can continue.', err);
        this.duplicateMatches = [];
      } finally {
        this.checkingDuplicates = false;
      }

      if (this.duplicateMatches.length) {
        return;
      }
    }

    this.saving = true;
    try {
      const customer = await this.customerService.createCustomer(payload);
      this.customerSelection.setSelected(customer, true);
      this.notification.showMessage(customer.pendingSync
        ? 'Customer saved offline. It will sync automatically later.'
        : 'Customer created successfully.');
      await this.router.navigate(['/order']);
    } catch (err: any) {
      this.formError = err?.error?.message || err?.message || 'Unable to create customer.';
    } finally {
      this.saving = false;
    }
  }

  openMatchMap(match: CustomerDuplicateMatch): void {
    const latitude = Number(match.customer.latitude);
    const longitude = Number(match.customer.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || (latitude === 0 && longitude === 0)) return;
    window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank');
  }

  async useExisting(match: CustomerDuplicateMatch): Promise<void> {
    if (!this.selectedRoute || !this.selectedSegmentId) return;
    this.saving = true;
    this.formError = '';
    try {
      const segment = this.segments.find(s => s.id === this.selectedSegmentId);
      const customer = await this.customerService.assignCustomerToSegment(
        match.customer,
        this.selectedSegmentId,
        this.selectedRoute.route,
        segment?.name || ''
      );
      this.customerSelection.setSelected(customer, true);
      this.notification.showMessage('Existing customer added to this route.');
      await this.router.navigate(['/order']);
    } catch (err: any) {
      this.formError = err?.error?.message || err?.message || 'Unable to add customer to this route.';
    } finally {
      this.saving = false;
    }
  }

  back(): void {
    this.router.navigate(['/customers']);
  }

  distanceLabel(match: CustomerDuplicateMatch): string {
    if (match.distanceM == null) return '';
    return match.distanceM < 1000
      ? `${Math.round(match.distanceM)} m away`
      : `${(match.distanceM / 1000).toFixed(1)} km away`;
  }

  private buildPayload(): CustomerCreateInput {
    const segment = this.segments.find(s => s.id === this.selectedSegmentId);
    const pricelist = this.priceLists.find(p => p.id === this.selectedPriceListId);
    const selectedTags = this.tags.filter(tag => this.selectedTagIds.includes(tag.id));
    return {
      clientCustomerUuid: this.clientCustomerUuid,
      name: this.name.trim().toLocaleUpperCase(),
      phone: this.phone.trim(),
      whatsappNumber: this.whatsappNumber.trim(),
      vat: this.vat.trim().toUpperCase().replace(/\s+/g, ''),
      gstTreatment: this.gstTreatment,
      tagIds: [...this.selectedTagIds],
      tagNames: selectedTags.map(tag => tag.name),
      street: this.street.trim(),
      street2: this.street2.trim(),
      city: this.city.trim(),
      zip: this.zip.trim(),
      countryId: this.countryId,
      stateId: this.selectedStateId,
      latitude: this.latitude,
      longitude: this.longitude,
      gpsAccuracyM: this.gpsAccuracyM,
      gpsCapturedAt: this.gpsCapturedAt,
      routeId: this.selectedRoute?.id || null,
      routeSegmentId: this.selectedSegmentId,
      routeName: this.selectedRoute?.route || '',
      segmentName: segment?.name || '',
      priceListId: this.selectedPriceListId,
      priceListName: pricelist?.pricelist_name || '',
      image: this.image,
      imageMimeType: this.imageMimeType,
    };
  }

  private compressImage(file: File, maxSide: number, quality: number): Promise<{ base64: string; mimeType: string }> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const url = URL.createObjectURL(file);
      image.onload = () => {
        try {
          const longest = Math.max(image.naturalWidth, image.naturalHeight);
          const scale = longest > maxSide ? maxSide / longest : 1;
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
          canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas unavailable');
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve({ base64: dataUrl.split(',', 2)[1], mimeType: 'image/jpeg' });
        } catch (err) {
          reject(err);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Unable to read image.'));
      };
      image.src = url;
    });
  }
}
